// Front/src/pages/NewReport.js

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../style/NewReport.css';
import { useAuthContext } from "../hooks/useAuthContext"; // Import Auth Context
import { upload } from "../api"; // import upload function from `api.js`
import { get_scale_from_webapi, get_thermometer_id_from_webapi, get_weight_sets_ids_from_webapi, get_latest_scale_report } from "../api"; // to get scale, thermometer and scale report.
import 'react-toastify/dist/ReactToastify.css'; // For pop-up notifications
import { toast } from 'react-toastify'; // For pop-up notifications
import { ToastContainer } from 'react-toastify'; // For pop-up notifications
import { Helmet } from "react-helmet-async"; // For name in the brouser
import { validateFormData } from "../utils/reportFormValidation"; // Import validation function
import { generateReportJson } from "../utils/generateReportJson"; // Import JSON generator function
import { useEffect } from 'react'; // effect to change values
import AccuracyClassTable from './AccuracyTable'; // for allowed deviation
import DivisionTable from './DivisionTable'; // for new division table
import { useLanguageContext } from '../context/LanguageContext'; // for using right to left and left to right
import { useTranslate } from '../hooks/useTranslate'; // for translating to hebrew and english
import { getRecommendedCornerWeight, getRecommendedTareWeights, getRecommendedLinearityWeights, getRankAndMinFromNandD  } from "../utils/mathHelper"; // to calculate recommended values
import { isWithinDeviation } from "../utils/accuracyHelpers"; // to color in green or red depending on the deviation
import { getFieldValidationClass } from "../utils/reportFormValidation"; // import the validation helper for red borders
import Swal from 'sweetalert2'; // for pop up on the screen to update report.


const NewReport = () => {
    const location = useLocation();
    
    const storedNavState = JSON.parse(localStorage.getItem("newReportNavState") || "{}");
    const navState = {
        ...storedNavState,
        ...location.state,
    };

    const {
        DOCNO, MAKAT, itemCode, KLINE,
        SERNUM, SERN, custId, address, PDATE,
        SAMLIL, serialNumber, isSymboled, fromSearch
    } = navState;

    const navigate = useNavigate();
    const { user } = useAuthContext(); // Get user from AuthContext
    const userId = user?.id || null; // Fallback to null if no user
    const agentCode = navState?.AGENTCODE || location?.state?.AGENTCODE || userId;

    const [showAccuracyTable, setShowAccuracyTable] = useState(false); // toggle to show the allowed deviations
    const [scaleExists, setScaleExists] = useState(null); // null = not checked yet
    const [isUploading, setIsUploading] = useState(false); // Loading screen
    const [isChecking, setIsChecking] = useState(false);
    const [hasRunInitialCheck, setHasRunInitialCheck] = useState(false); // Only run initial scale check ONCE after page load (not on every keypress)
    const [hasMergedFormData, setHasMergedFormData] = useState(false); // Track whether the form data (localStorage + location.state) has been merged
    const [hasSubmitted, setHasSubmitted] = useState(false); // for disabling the submit button after successful submit.
    const [showDivisionTable, setShowDivisionTable] = useState(false); // for division table

    // Post error to avoid code duplications
    const PostError = useCallback((message) => {
        toast.error(message, {
            icon: false, // Removes the default tick icon
            autoClose: 3000, // 3 seconds (default)
        });

        // for debugging
        // setMessage(message);
        // setMessageType("error");
        // console.error(message);
    }, []);

    // for debugging
    //console.log("fromSearch = ", fromSearch);

    // for debugging
    // const [message, setMessage] = useState(""); // for showing messages on the screen
    // const [messageType, setMessageType] = useState(""); 

    const t = useTranslate(); // 💬 Get translations
    const { language } = useLanguageContext(); // 🌐 Get current language

    // go back to mytasks if data is broken from location
    useEffect(() => {
        const storedNavState = JSON.parse(localStorage.getItem("newReportNavState") || "{}");
        const navState = {
            ...storedNavState,
            ...location.state
        };

        // for debugging:
        // console.log("navState?.DOCNO = ", navState?.DOCNO);
        // console.log("navState?.KLINE = ", navState?.KLINE);
        // console.log("navState?.SERN = ", navState?.SERN);
        if (!navState?.DOCNO || !navState?.KLINE || !navState?.SERN) {
            toast.error(t.missingNavigationData || "Missing required data");
            if (navState.fromSearch) {
                navigate("/searchservicecall");
            } else {
                    navigate("/mytasks");
                }
        }
    }, [location.state, navigate, t]);

    const requiredFields = [

        // From search:
        "calibratorId",
        "thermometerId",
        "weightSet1",
        "weightSet2",

        // Basic info
        "thermometerId",
        "scaleSerialId",
        "address",
        "linearityTest",
        "cornersTest",
        "repeatabilityTest",
        "tareTest",
        "weightValue",
        "loadValue",
        "symbolledPrinting",
        "nextCalibrationMonths",
        "temperatureStart",
        "temperatureEnd",
        "humidityStart",
        "humidityEnd",
    
        // Scale auto-filled details
        "weightUnits",
        "accuracyClass",
        "dValue",
        "eValue",
        "maxValue",
        "minValue",
        "nValue",
    
        // Review summary fields
        "visualReview",
        "resetMechanism",
        "tareMechanism",
        "leveling",
        "printing",
    
        // Additional settings
        "useLoadingPermission",
        "loadingPermissionValue",
        "useCheckUpTo",
        "checkUpToValue",
        "calibrationApproval",
    
        // Corners test
        "cornerWeightValue",
        "cornerTopLeft",
        "cornerTopRight",
        "cornerBottomLeft",
        "cornerBottomRight",
    ];

    const defaultRows = 6;
    const linearityTotalRows = defaultRows;
    const repeatabilityTotalRows = defaultRows;

    // for red borders
    for (let i = 1; i <= linearityTotalRows; i++) {
        requiredFields.push(`linearityWeight${i}`, `linearityLoad${i}`, `linearityUnload${i}`);
    }
    for (let i = 1; i <= repeatabilityTotalRows; i++) {
        requiredFields.push(`repeatWeight${i}`, `repeatLoad${i}`);
    }    
  
    const didFetchThermometerRef = useRef(false);
    const handleCheckThermometer = useCallback(async () => {
        if (didFetchThermometerRef.current) return;

        // for debugging
        // setMessage("");
        // setMessageType("");

        if (!userId) {
            PostError(t.errorUserNotLoggedIn);
            return;
        }
    
        setIsChecking(true);
    
        try {
            // fetch thermometer from Docnet for this calibrator
            const thermometer_response = await get_thermometer_id_from_webapi(agentCode);
    
            // Ensure data is an array before searching; otherwise avoid .find() errors (Safe check: only search if data is an array)
            const thermometer = Array.isArray(thermometer_response?.data)
            ? thermometer_response.data.find(item => item?.id)
            : null;

            if (thermometer?.id) {
                setFormData(prevData => ({
                    ...prevData,
                    thermometerId: thermometer.id
                }));
                // Only mark as fetched on success
                didFetchThermometerRef.current = true;
            } else {
                PostError(t.errorThermometer);
            }
    
        } catch (error) {
            PostError(`${t.errorThermometerGeneral} ${error.message}`);
        } finally {
            setIsChecking(false);
        }
    }, [userId, t, PostError, agentCode]); // Add userId as a dependency if it's used inside
    
    const handleSetWeights = useCallback(async () => {

        // Clear previous messages before running validations // for debugging
        // setMessage("");
        // setMessageType("");
        try {
            
            // Look up weight sets for this calibrator.
            const technicianIdWeightSets = await get_weight_sets_ids_from_webapi(agentCode);

            // Ensure the response data is an array before trying to filter; if not, use empty array
            const weightSetsData = Array.isArray(technicianIdWeightSets?.data)
            ? technicianIdWeightSets.data
            : [];

            // Find ALL weight sets matching agent code with non-null id
            const matchingWeightSets = weightSetsData.filter(ws =>
                ws?.id &&
                (
                    ws.WeightSetName?.HebrewString?.includes(agentCode) ||
                    ws.WeightSetName?.EnglishString?.includes(agentCode)
                )
            );

            const weightSet1 = matchingWeightSets[0]?.id || '';
            const weightSet2 = matchingWeightSets[1]?.id || '';

            // if no weight sets were found post an error.
            if (!weightSet1 && !weightSet2) {
                PostError(t.errorWeightSetsNoMatch);
            }

            // Update state
            setFormData(prevData => ({
                ...prevData,
                weightSet1,
                weightSet2
            }));
        } catch (error) {
            PostError(`${t.errorWeightSets} ${error.message}`);
        }
    }, [agentCode, t, PostError]);

    // for saving the user data input
    const savedRaw = localStorage.getItem("newReportFormData");
    let savedFormData = {};
    try {
        const parsed = JSON.parse(savedRaw);
        // for debugging:
        // console.log("parsed?.DOCNO = ", parsed?.DOCNO);
        // console.log("navState?.DOCNO = ", navState?.DOCNO);
        // console.log("parsed?.SERN = ", parsed?.SERN);
        // console.log("navState?.SERN = ", navState?.SERN);
        if (parsed?.DOCNO === navState?.DOCNO && parsed?.SERN === navState?.SERN) {
            savedFormData = parsed;
        } else {
            // Clear stored data
            ["newReportFormData", "newScaleNavState", "newScaleTypeNavState", "newReportNavState", "newClientFormData"]
            .forEach(key => localStorage.removeItem(key));
        }
    } catch (e) {
        // Clear stored data
        ["newReportFormData", "newScaleNavState", "newScaleTypeNavState", "newReportNavState", "newClientFormData"]
        .forEach(key => localStorage.removeItem(key));
    }


    // for debugging
    // console.log("location.state?.isSymboled = ", location.state?.isSymboled);
    // console.log("SAMLIL = ", SAMLIL);

    // Default state values for the form
    const defaultData = {
        calibratorId: location.state?.AGENTCODE || userId || '',
        thermometerId: '',
        weightSet1: '',
        weightSet2: '',
        useCylinderSet1: false,
        useCylinderSet2: false,
        reportType: 'Service',
        scaleSerialId: location.state?.serialNumber || serialNumber || SERNUM,
        address: address,
        linearityTest: true,
        cornersTest: true,
        repeatabilityTest: true,
        tareTest: true,
        weightValue: '',
        loadValue: '',
        symbolledPrinting: isSymboled ?? SAMLIL, // Fix: use latest from MyTasks
        nextCalibrationMonths: 12,
        temperatureStart: '',
        temperatureEnd: '',
        humidityStart: '',
        humidityEnd: '',

        date: location.state?.PDATE || PDATE || '',
    
        // New fields for scale to be filled automatically
        weightUnits: '',
        accuracyClass: '',
        dValue: '',
        eValue: '',
        maxValue: '',
        minValue: '',
        nValue: '',
    
        // Review Summary Fields
        visualReview: "OK",
        resetMechanism: "OK",
        tareMechanism: "OK",
        leveling: "OK",
        printing: "NotExist",
        comments: "",
    
        // General Comments
        useLoadingPermission: false,
        loadingPermissionValue: '',
        useCheckUpTo: false,
        checkUpToValue: '',
        calibrationApproval: false,

        // General info
        DOCNO: location.state?.DOCNO || DOCNO || '',
        SERN: location.state?.SERN || SERN || '',
    
        // Initialize separate state for Linearity Test
        ...Array.from({ length: linearityTotalRows }, (_, i) => ({
        [`linearityWeight${i + 1}`]: '',
        [`linearityLoad${i + 1}`]: '',
        [`linearityUnload${i + 1}`]: '',
        })).reduce((acc, obj) => ({ ...acc, ...obj }), {}),
    
        // make the first box 0
        linearityWeight1: '0',
    
        // Initialize separate state for Repeatability Test
        ...Array.from({ length: repeatabilityTotalRows }, (_, i) => ({
        [`repeatWeight${i + 1}`]: '',
        [`repeatLoad${i + 1}`]: '',
        })).reduce((acc, obj) => ({ ...acc, ...obj }), {})
    };
    
    // Merge saved form data (if any) to override defaults
    const [formData, setFormData] = useState(() => ({
        ...defaultData,
        ...savedFormData, // This overrides defaults with saved values
        symbolledPrinting: location.state?.isSymboled ?? SAMLIL // Always override this one
    }));


    // Step 1: Merge saved form data and override with location.state values
    useEffect(() => {
        const saved = localStorage.getItem("newReportFormData");
        if (saved) {
        setFormData(prev => ({
            ...prev,
            ...JSON.parse(saved),
        }));
        }
        setHasMergedFormData(true); // Mark merge complete
    }, []);
    
    // Step 2: Insert scaleSerialId from navigation state (e.g., from NewScale)
    useEffect(() => {
    if (location.state?.serialNumber) {
        setFormData(prev => ({
        ...prev,
        scaleSerialId: location.state.serialNumber,
        }));
    }
    }, [location.state?.serialNumber]);
    
    // save the state to localStorage or context on load
    useEffect(() => {
        if (location.state) {
          localStorage.setItem("newReportNavState", JSON.stringify(location.state));
        }
    }, [location.state]);
         
    // Save form data on every change
    useEffect(() => {
        localStorage.setItem("newReportFormData", JSON.stringify(formData));
    }, [formData]);

    // for red borders
    const getRequiredInputClass = (name) => {

        // Explicit red border if scale is marked as not existing (Scale not found)
        if (name === "scaleSerialId" && scaleExists === false) {
            return "invalid-input";
        }
    
        // General required fields check (Required field is empty)
        if (requiredFields.includes(name) && (!formData[name] || formData[name].toString().trim() === "")) {
            return "invalid-input";
        }
    
        // Additional validation specifically for eValue: eValue-specific logic (must exist and be >= dValue)
        if (name === "eValue") {
            const e = parseFloat(formData.eValue);
            const d = parseFloat(formData.dValue);
    
            // If empty
            if (formData.eValue.toString().trim() === "") {
                return "invalid-input";
            }
    
            // If numeric but less than dValue
            if (!isNaN(e) && !isNaN(d) && e < d) {
                return "invalid-input";
            }
        }

        // Additional validation for d-related logic (dValue-based test field validation)
        return getFieldValidationClass(name, formData[name], formData);
    };

    useEffect(() => {
        const newTotal = formData.accuracyClass === 3 || formData.accuracyClass === 4 ? 3 : defaultRows;
    
        setFormData(prevData => {
            const updatedData = { ...prevData };
    
            // Add missing fields (if increasing back to 6)
            for (let i = 1; i <= defaultRows; i++) {
                if (updatedData[`repeatWeight${i}`] === undefined) {
                    updatedData[`repeatWeight${i}`] = '';
                }
                if (updatedData[`repeatLoad${i}`] === undefined) {
                    updatedData[`repeatLoad${i}`] = '';
                }
            }
    
            // Trim excess fields if reducing
            for (let i = newTotal + 1; i <= defaultRows; i++) {
                updatedData[`repeatWeight${i}`] = '';
                updatedData[`repeatLoad${i}`] = '';
            }
    
            return updatedData;
        });
    }, [formData.accuracyClass]);    
    
    const updateLinearityWeight6 = (maxVal) => {
        const parsedMax = parseFloat(maxVal);
        if (!isNaN(parsedMax)) {
            setFormData((prevData) => ({
                ...prevData,
                linearityWeight6: parsedMax.toString(),
            }));
        }
    };    

    const updateRepeatabilityWeights = (maxVal) => {
        const parsedMax = parseFloat(maxVal);
        if (!isNaN(parsedMax)) {
            const eightyPercent = Math.round(0.8 * parsedMax).toString(); //rounding to value
            setFormData((prevData) => ({
                ...prevData,
                repeatWeight1: eightyPercent,
                repeatWeight2: eightyPercent,
                repeatWeight3: eightyPercent,
            }));
        }
    };    

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
    
        setFormData((prevData) => {
            let updatedData = {
                ...prevData,
                [name]: newValue,
            };

            // When linearityLoad6 changes → update linearityUnload6
            if (name === "linearityLoad6") {
                updatedData.linearityUnload6 = newValue;
            }
    
            // Whenever maxValue or eValue changes, update nValue
            if (name === "maxValue" || name === "eValue") {
                const max = parseFloat(name === "maxValue" ? value : prevData.maxValue);
                const e = parseFloat(name === "eValue" ? value : prevData.eValue);
    
                if (!isNaN(max) && !isNaN(e) && e !== 0) {
                    updatedData.nValue = Math.round(max / e).toString(); // calculate and round
                } else {
                    updatedData.nValue = "";
                }
            }

            // When temperatureStart changes, set temperatureEnd to same value
            if (name === "temperatureStart") {
                updatedData.temperatureEnd = newValue;
            }

            // When humidityStart changes, set humidityEnd to same value
            if (name === "humidityStart") {
                updatedData.humidityEnd = newValue;
            }

            // 80% weights logic
            if (name === "repeatabilityTest" && newValue && prevData.maxValue) {
                updatedData = {
                    ...updatedData,
                    repeatWeight1: Math.round(0.8 * parseFloat(prevData.maxValue)).toString(),
                    repeatWeight2: Math.round(0.8 * parseFloat(prevData.maxValue)).toString(),
                    repeatWeight3: Math.round(0.8 * parseFloat(prevData.maxValue)).toString(),
                };
            }
    
            // Update linearity when maxValue changes
            if (name === "maxValue") {
                updatedData.linearityWeight6 = value || "";
            }
    
            return updatedData;
        });
    };
    

    useEffect(() => {

        // wait for context to load
        if (user === undefined) {
            return <div className="loading-screen">Loading...</div>;
        }

        if (!userId) {
            PostError(t.errorRelog);
            navigate("/login"); // redirect
            return;
        }

        handleCheckThermometer(); // Automatically fetch thermometer ID
        handleSetWeights(); // Automatically fill weightSet1 + weightSet2

        if (formData.maxValue) {
            // Restore max if lines reduced
            updateLinearityWeight6(formData.maxValue);
        }
    }, [formData.maxValue, userId, user, navigate, handleCheckThermometer, handleSetWeights, t, PostError]); // ESLint happy
    
    useEffect(() => {
        if (formData.symbolledPrinting === "Yes" && !formData.repeatabilityTest) {
            setFormData(prev => ({
                ...prev,
                repeatabilityTest: true
            }));
        }
    }, [formData.symbolledPrinting, formData.repeatabilityTest]);    

    useEffect(() => {
        if (formData.minValue) {
            setFormData(prev => ({
                ...prev,
                linearityWeight2: formData.minValue
            }));
        }
    }, [formData.minValue]);
    
    useEffect(() => {
        const min = parseFloat(formData.minValue);
        const max = parseFloat(formData.maxValue);
        const e = parseFloat(formData.eValue);
        const accClass = parseInt(formData.accuracyClass);
    
        const weights = getRecommendedLinearityWeights({ min, max, e, accuracyClass: accClass });
        if (weights) {
            setFormData(prev => ({ ...prev, ...weights }));
        }
    }, [formData.minValue, formData.maxValue, formData.eValue, formData.accuracyClass]);

    // for checking if scale exists in docnet
    const handleCheckScale = useCallback(async () => {
    
        // for debugging
        // Clear previous messages before running validations
        // setMessage("");
        // setMessageType("");
        // console.log("formData.scaleSerialId = ", formData.scaleSerialId);

        if (!formData.scaleSerialId.trim()) {
            PostError(t.errorScaleIdEmpty);
            return;
        }
    
        setIsChecking(true); // Show loading state
    
        try {
            const scaleData = await get_scale_from_webapi(userId, formData.scaleSerialId);
            
            // for debugging
            // console.log("scaleData = ", scaleData);

            if (!scaleData.ScaleType) {
                PostError(`${t.errorScaleNotFound}`);
                setScaleExists(false); // mark as not existing
                setIsChecking(false); // Stop Loading
                return;
            }

            // for debugging
            // console.log("scaleData.Owner.Id = ", scaleData.Owner.Id);
            // console.log("custId = ", custId);
            if (scaleData.Owner.Id !== custId) {
                PostError(`${t.errorScaleOtherClient}`);
                setScaleExists(false); // mark as not existing
                setIsChecking(false); // Stop Loading
                return;
            }

            if (scaleData.Owner.Id === custId) {

                toast.success(t.successScaleFound, {
                    icon: false, // Removes the default tick icon
                    autoClose: 3000, // 3 seconds (default)
                });
            
                setScaleExists(true); // mark as existing

                // Extract required data from the API response
                const { ScaleType } = scaleData;
                const scaleAccuracy = ScaleType?.ScaleAccuracy || {};

                // for debugging
                //console.log("ScaleType = ", ScaleType);
                // console.log("scaleData = ", scaleData);
                // console.log("scaleData?.InternalId = ", scaleData?.InternalId);

                // Update form fields with extracted values
                setFormData(prevData => ({
                    ...prevData,
                    internalId: scaleData?.InternalId || "",
                    weightUnits: scaleAccuracy.WeightUnits || "",
                    accuracyClass: Number(4 - scaleAccuracy?.AccuracyClass) || "",
                    dValue: scaleAccuracy.D || "",
                    eValue: scaleAccuracy.E || "",
                    maxValue: scaleAccuracy.Max || "",
                    minValue: scaleAccuracy.Min || "",
                    nValue: scaleAccuracy.N || "",
                }));

                // Trigger logic based on newly set max value
                const maxFromScale = scaleAccuracy.Max;
                if (maxFromScale) {
                    updateLinearityWeight6(maxFromScale);
                }
                if (formData.repeatabilityTest && maxFromScale) {
                    updateRepeatabilityWeights(maxFromScale);
                }

                if (formData.tareTest && maxFromScale) {
                    const { weightValue } = getRecommendedTareWeights({ max: maxFromScale }) || {};
                    setFormData(prev => ({
                        ...prev,
                        weightValue: weightValue || ''
                    }));
                }
                
                const { cornerWeightValue } = getRecommendedCornerWeight({ max: maxFromScale }) || {};
                setFormData(prev => ({
                    ...prev,
                    cornerWeightValue: cornerWeightValue || ''
                }));
                
            } else if (scaleData.status === 404){
                PostError(t.errorScaleNotFound);
                setScaleExists(false); // mark as not existing
            }
            else {
                PostError(t.errorCheckingScale);
            }
        } catch (error) {
            PostError(t.errorCheckingScale);
        } finally {
            setIsChecking(false); // Hide loading state
        }
    }, [
        formData.scaleSerialId,
        formData.repeatabilityTest,
        formData.tareTest,
        userId,
        custId,
        t,
        PostError
    ]); 
    
    // Run scale check ONCE after merge and if serial ID is valid
    useEffect(() => {
        if (
            hasMergedFormData &&
            formData.scaleSerialId?.trim() &&
            !hasRunInitialCheck
        ) {
            handleCheckScale();
            setHasRunInitialCheck(true);
        }
    }, [hasMergedFormData, formData.scaleSerialId, hasRunInitialCheck, handleCheckScale]);    

    // effect to calculate rank and accuracy class
    useEffect(() => {
        const n = parseFloat(formData.nValue);
        const d = parseFloat(formData.dValue);
    
        if (!isNaN(n) && !isNaN(d) && n > 0 && d > 0) {
            const result = getRankAndMinFromNandD(n, d);
            if (result) {
                setFormData(prev => ({
                    ...prev,
                    accuracyClass: result.accuracyClass,
                    minValue: result.min.toString()
                }));
            }
        }
    }, [formData.nValue, formData.dValue]);    

    useEffect(() => {
        const handlePopState = () => {
            // Clear stored data
            ["newReportFormData", "newScaleNavState", "newScaleTypeNavState", "newReportNavState", "newClientFormData"]
            .forEach(key => localStorage.removeItem(key));
        };
        
        window.addEventListener("popstate", handlePopState);
        
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    const handleSubmit = async (e) => {

        e.preventDefault();

        // Clear previous messages before running validations // for debugging
        // setMessage("");
        // setMessageType("");

        // Validate form data
        const validationError = validateFormData(formData, t);
        if (validationError) {
            PostError(validationError);
            return; // Stop form submission
        }
        
        let isUpdate = false;
        // for debugging
        // console.log("itemCode = ", itemCode);
        // console.log("MAKAT = ", MAKAT);
        
        setIsUploading(true); // Show loading message
        let idToUpdate = "";

        try {
            const check_exists_response = await get_latest_scale_report(formData.scaleSerialId, navState.DOCNO, navState.SERN);
        
            if (check_exists_response && !check_exists_response?.error) {
                const result = await Swal.fire({ // This should be a user-friendly string like "Report already exists. Update it?"
                    title: t.reportExists,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: t.yes || 'Yes',
                    cancelButtonText: t.no || 'No',
                    focusCancel: true,
                    backdrop: true,
                    customClass: {
                        confirmButton: 'swal2-confirm btn-green',
                        cancelButton: 'swal2-cancel btn-red'
                    },
                    didOpen: () => {
                        const popup = document.querySelector('.swal2-popup');
                        if (popup) {
                            popup.setAttribute('dir', language === 'he' ? 'rtl' : 'ltr');
                        }
                    }
                });
        
                if (result.isConfirmed) {
                    isUpdate = true;
                    idToUpdate = check_exists_response?.id;
                    // Continue to upload logic here (e.g. call uploadReport())
                } else {
                    setIsUploading(false);
                    return; // Stop if user cancels
                }
            }
            // Continue execution if no report exists or user confirmed overwrite
        } catch (error) {
            setIsUploading(false);
            PostError(`${t.errorCheckReport} ${error.message}`);
        }        
    
        try {

            // NEW SAFETY CHECK HERE:
            if (!navState.DOCNO || !navState.KLINE || !navState.SERN) {
                PostError(t.missingNavigationData || "Missing required navigation data");

                const goTo = navState.fromSearch ? "/searchservicecall" : "/mytasks";

                ["newReportFormData", "newScaleNavState", "newScaleTypeNavState", "newReportNavState", "newClientFormData"]
                    .forEach(key => localStorage.removeItem(key));

                navigate(goTo);
                return;
            }

            // Generate JSON Data using utility function
            const jsonData = generateReportJson(
                formData,
                userId,
                linearityTotalRows,
                repeatabilityTotalRows,
                navState.DOCNO,
                navState.MAKAT,
                navState.KLINE,
                navState.SERNUM,
                navState.SERN,
                isUpdate,
                idToUpdate,
            );

            // Upload report
            let upload_response = false;
            upload_response = await upload(jsonData, userId);

            // ✅ Read and return the JSON response
            const message = upload_response?.error || upload_response.body?.error || upload_response.body?.Error 
                            || upload_response.body?.Message || upload_response.body?.message 
                            || "No message found";

            if (upload_response.status !== 200) { // Check if status is NOT 200 (failed)
                setIsUploading(false); // Hide loading message
                // for debugging
                // setMessage(`${t.errorUpload}\n (${message})`);
                // setMessageType("error");
                // console.error(`${t.errorUpload}\n (${message})`);
                toast.error(
                    <>
                      {t.errorUpload}
                      <br />
                      {message}
                    </>,
                    { 
                        icon: false, // Removes the default tick icon
                        autoClose: 3000, // 3 seconds (default)
                    }
                );
                return;
            }

            if (upload_response.status === 200) { // Check if status is 200 (Success)
                setIsUploading(false); // Hide loading message
                setHasSubmitted(true); // Disable the button
                // for debugging
                // setMessage(t.successUpload);
                // setMessageType("success");

                // Show success toast and navigate after it closes
                toast.success(t.successUpload, {
                    icon: false,
                    onClose: () => {
                        // Only remove AFTER you've used navState
                        const goTo = navState.fromSearch ? "/searchservicecall" : "/mytasks";

                        // Clear stored data
                        ["newReportFormData", "newScaleNavState", "newScaleTypeNavState", "newReportNavState", "newClientFormData"]
                            .forEach(key => localStorage.removeItem(key));
                
                        navigate(goTo);
                    },
                    autoClose: 3000,
                });
            }

            return
        
        } catch (error) {
            setIsUploading(false); // Hide loading message
            PostError(`${t.errorSubmit} ${error.message}`)
        }
    };
    
    const getDeviationClass = (weight, actual, customContext = null) => {
        // Special case for Tare Test load value (must be negative to be considered OK)
        if (customContext === 'tare') {
            const parsedActual = parseFloat(actual);
            if (isNaN(parsedActual) || parsedActual >= 0) {
                return "outside-deviation";
            }
        }
    
        // General logic
        return isWithinDeviation({
            weight,
            actual,
            eValue: formData.eValue,
            accuracyClass: formData.accuracyClass,
            reportType: formData.reportType
        }) ? "within-deviation" : "outside-deviation";
    };
    

    return (
        <div className="new-report-container" dir={language === 'he' ? 'rtl' : 'ltr'}>

            {/* Helmet to dynamically change the title */}
            <Helmet>
                <title>Shekel Technet - New Report</title>
            </Helmet>

            <button 
                type="button" 
                className="button-go-back"
                onClick={() => {
                    const navState = location.state || JSON.parse(localStorage.getItem("newReportNavState") || "{}");
                    const goTo = navState.fromSearch ? "/searchservicecall" : "/mytasks";

                    // Clear stored data
                    ["newReportFormData", "newScaleNavState", "newScaleTypeNavState", "newReportNavState", "newClientFormData"]
                        .forEach(key => localStorage.removeItem(key));

                    navigate(goTo);
                }}
            >
                {t.goBack}
            </button>

            <h2>{t.createReport}</h2>

            <form onSubmit={handleSubmit}>
                
                {/* Show Calibrator ID if we got from search */}
                {fromSearch && (
                <>
                    <label>{t.calibratorId}</label>
                    <input
                    type="text"
                    name="calibratorId"
                    value={formData.calibratorId}
                    onChange={handleChange}
                    readOnly={!fromSearch} // editable only if from search 
                    />
                </>
                )}

                {/* Thermometer ID */}
                <label>{t.thermometerId}</label>
                <input 
                    type="number" 
                    name="thermometerId" 
                    value={formData.thermometerId || ''} 
                    onChange={handleChange}
                    readOnly={!fromSearch} // editable only if from search 
                    placeholder={t.enterThermometer}
                    className={`numeric-input ${getRequiredInputClass("thermometerId")}`}
                />

                {/* Weight Sets Table */}
                <label>{t.weightSets}</label>
                <div className="weight-cylinder-wrapper">
                    <div className="weight-cylinder-flex">
                        {/* Weight Set Inputs */}
                        <div className="weight-input-pair">
                            <input 
                                type="number" 
                                name="weightSet1" 
                                value={formData.weightSet1 || ''} 
                                onChange={handleChange} 
                                placeholder={t.set1}
                                readOnly={!fromSearch} // editable only if from search 
                                className={`weight-input numeric-input ${getRequiredInputClass("weightSet1")}`}
                            />
                            <input 
                                type="number" 
                                name="weightSet2" 
                                value={formData.weightSet2 || ''} 
                                onChange={handleChange} 
                                placeholder={t.set2}
                                readOnly={!fromSearch} // editable only if from search 
                                className={`weight-input numeric-input ${getRequiredInputClass("weightSet2")}`}
                            />
                        </div>

                        {/* Cylinder Set Checkboxes - Vertical Stack */}
                        <div className="cylinder-checkbox-stack">
                            <label className="use-cylinder-1-label cylinder-label">
                                <input 
                                    type="checkbox" 
                                    name="useCylinderSet1" 
                                    checked={formData.useCylinderSet1 || false} 
                                    onChange={handleChange} 
                                />
                                {t.useCylinder1}
                            </label>

                            <label className="cylinder-label">
                                <input 
                                    type="checkbox" 
                                    name="useCylinderSet2" 
                                    checked={formData.useCylinderSet2 || false} 
                                    onChange={handleChange} 
                                />
                                {t.useCylinder2}
                            </label>
                        </div>
                    </div>
                </div>

                {/* Report Type */}
                <label className="report-type-label">{t.reportType}</label>
                <select name="reportType" value={formData.reportType} onChange={handleChange}>
                    <option value="Service">{t.service}</option>
                    <option value="Marketing">{t.marketing}</option>
                </select>

                {/* Scale Serial ID */}
                <label>{t.scaleSerialId}</label>
                <div className="scale-id-container">
                    <input 
                        type="text" 
                        name="scaleSerialId" 
                        value={formData.scaleSerialId} 
                        onChange={handleChange} 
                        placeholder={t.enterScaleSerial} 
                        className={`numeric-input ${getRequiredInputClass("scaleSerialId")}`}
                    />
                    <button 
                        type="button" 
                        className="check-scale-btn" 
                        onClick={handleCheckScale}
                        disabled={isChecking}
                    >
                        {isChecking ? t.checking : t.check}
                    </button>
                
                    {scaleExists === false && (
                        <button
                            type="button"
                            onClick={() => navigate("/newscale", {
                                state: {
                                    ...navState,
                                    serialId: formData.scaleSerialId,
                                    SERN: SERN,
                                    SERNUM: SERNUM,
                                    custId: custId,
                                    MAKAT: MAKAT,
                                    itemCode: itemCode,
                                    address: address,
                                    DOCNO: DOCNO,
                                    KLINE: KLINE,
                                    SAMLIL: SAMLIL,
                                    fromSearch: fromSearch,
                                    isSymboled: formData.symbolledPrinting
                                }
                            })}
                            className="create-scale-btn"
                        >
                            {t.createScale}
                        </button>
                    )}

                </div>

                    {/* Auto-filled Scale Details */}
                    {formData.weightUnits && (
                    <>
                        <label>{t.internalId}</label>
                        <input
                            type="text"
                            name="internalId"
                            value={formData.internalId}
                            onChange={handleChange}
                            className={`numeric-input ${getRequiredInputClass("internalId")}`}
                        />

                        <label>{t.address}</label>
                        <input type="text" name="address" value={formData.address} onChange={handleChange} className={getRequiredInputClass("address")}
                        />

                        <label>{t.weightUnit}</label>
                        <select 
                            name="weightUnits" 
                            value={
                                formData.weightUnits === 0 ? "mg" :
                                formData.weightUnits === 1 ? "g" :
                                formData.weightUnits === 2 ? "kg" :
                                formData.weightUnits === 3 ? "lb" :
                                ""
                            }
                            onChange={(e) => setFormData(prevData => ({
                                ...prevData, 
                                weightUnits: e.target.value // store the string directly
                            }))}
                        >
                            <option value="mg">{t.miligram}</option>
                            <option value="g">{t.gram}</option>
                            <option value="kg">{t.kilogram}</option>
                            <option value="lb">{t.lb}</option>
                        </select>
                        
                        <button 
                            type="button" 
                            onClick={() => setShowDivisionTable(prev => !prev)}
                            className="toggle-division-table-button"
                            >
                            {showDivisionTable ? t.hideDivisionTable : t.showDivisionTable}
                        </button>

                        {showDivisionTable && <DivisionTable />}

                        <label>{t.accuracyClass}</label>
                        <select 
                            name="accuracyClass" 
                            value={formData.accuracyClass === 1 ? "One" :
                                formData.accuracyClass === 2 ? "Two" :
                                formData.accuracyClass === 3 ? "Three" :
                                formData.accuracyClass === 4 ? "Four" : ""}
                            onChange={(e) => setFormData(prevData => ({
                                ...prevData, 
                                accuracyClass: e.target.value === "One" ? 1 :
                                            e.target.value === "Two" ? 2 :
                                            e.target.value === "Three" ? 3 :
                                            e.target.value === "Four" ? 4 : ""
                            }))}
                        >
                            <option value="">{t.selectAccuracyClass}</option>
                            <option value="One">{t.one}</option>
                            <option value="Two">{t.two}</option>
                            <option value="Three">{t.three}</option>
                            <option value="Four">{t.four}</option>
                        </select>

                        <label>{t.dValue}</label>
                        <input 
                            type="number"
                            name="dValue" 
                            value={formData.dValue}
                            onChange={handleChange}
                            className={`numeric-input ${getRequiredInputClass("dValue")}`}
                        />

                        <label>{t.eValue}</label>
                        <input 
                            type="number" 
                            name="eValue" 
                            value={formData.eValue} 
                            onChange={handleChange} 
                            className={`numeric-input ${getRequiredInputClass("eValue")}`}
                        />


                        <label>{t.maxValue}</label>
                        <input
                            type="number"
                            name="maxValue"
                            value={formData.maxValue}
                            onChange={handleChange}
                            className={`numeric-input ${getRequiredInputClass("maxValue")}`}
                        />

                        <label>{t.minValue}</label>
                        <input
                            type="number"
                            name="minValue"
                            value={formData.minValue}
                            onChange={handleChange}
                            className={`numeric-input ${getRequiredInputClass("minValue")}`}
                        />

                        <label>{t.nValue}</label>
                        <input 
                            type="number" 
                            name="nValue" 
                            value={formData.nValue} 
                            onChange={handleChange} 
                            readOnly
                            className={`bottom-spacing numeric-input ${getRequiredInputClass("nValue")}`}
                        />

                    </>
                )}

                {/* Temperature & Humidity Start Inputs */}
                <label>{t.temperatureStart}</label>
                <input 
                    type="number" 
                    name="temperatureStart" 
                    value={formData.temperatureStart} 
                    onChange={handleChange} 
                    placeholder={t.enterTemperatureStart}
                    className={`numeric-input ${getRequiredInputClass("temperatureStart")}`}
                />

                <label>{t.humidityStart}</label>
                <input 
                    type="number" 
                    name="humidityStart" 
                    value={formData.humidityStart} 
                    onChange={handleChange} 
                    placeholder={t.enterHumidityStart}
                    className={`numeric-input ${getRequiredInputClass("humidityStart")}`}
                />

                {/* Tests in One Line */}
                <div className="tests-spacer"></div>
                <h3>{t.tests}</h3>

                <div className="test-group">
                    <label>
                        <input 
                            type="checkbox" 
                            name="linearityTest" 
                            checked={formData.linearityTest} 
                            onChange={handleChange} 
                        />
                        {t.linearity}
                    </label>

                    <label>
                        <input 
                            type="checkbox" 
                            name="cornersTest" 
                            checked={formData.cornersTest} 
                            onChange={handleChange} 
                        />
                        {t.corners}
                    </label>

                    <label>
                        <input 
                            type="checkbox" 
                            name="repeatabilityTest" 
                            checked={formData.repeatabilityTest} 
                            onChange={handleChange} 
                        />
                        {t.repeatability}
                    </label>

                    <label>
                        <input 
                            type="checkbox" 
                            name="tareTest" 
                            checked={formData.tareTest} 
                            onChange={handleChange} 
                        />
                        {t.tare}
                    </label>
                </div>

                {/* Conditionally Rendered Fields for Linearity Test */}
                {formData.linearityTest && (
                    <>
                        <label>{t.linearityTest}</label>
                        <div className="linearity-header-row">
                            <label>{t.linearityTest}</label>
                            <button
                                type="button"
                                onClick={() => setShowAccuracyTable(prev => !prev)}
                                className="deviation-btn"
                            >
                                {t.allowedDeviation}
                            </button>
                        </div>
                        {showAccuracyTable && (
                            <AccuracyClassTable
                                accuracyClass={formData.accuracyClass}
                                reportType={formData.reportType}
                                eValue={formData.eValue}
                            />
                        )}
                        
                        {/* Checkbox and text on the same line */}
                        {/* <div className="checkbox-inline">
                            <input type="checkbox" checked={linearityAddMoreLines} onChange={(e) => setLinearityAddMoreLines(e.target.checked)} />
                            <span>{t.addMoreLines}</span>
                        </div> */}

                        <div className="linearity-grid">
                            {[...Array(linearityTotalRows)].map((_, index) => (
                                <React.Fragment key={index}>
                                    <input 
                                        type="number" 
                                        name={`linearityWeight${index + 1}`}
                                        value={formData[`linearityWeight${index + 1}`] || ""}
                                        onChange={handleChange} 
                                        placeholder={`${t.weight} ${index + 1}`}
                                        className={`numeric-input ${getRequiredInputClass(`linearityWeight${index + 1}`)}`}
                                    />
                                    <input
                                        type="number"
                                        name={`linearityLoad${index + 1}`}
                                        value={formData[`linearityLoad${index + 1}`] || ""}
                                        onChange={handleChange}
                                        placeholder={`${t.load} ${index + 1}`}
                                        className={`numeric-input ${getDeviationClass(formData[`linearityWeight${index + 1}`], formData[`linearityLoad${index + 1}`])} ${getRequiredInputClass(`linearityLoad${index + 1}`)}`}
                                    />
                                    <input
                                        type="number"
                                        name={`linearityUnload${index + 1}`}
                                        value={formData[`linearityUnload${index + 1}`] || ""}
                                        onChange={handleChange}
                                        placeholder={`${t.unload} ${index + 1}`}
                                        className={`numeric-input ${getDeviationClass(formData[`linearityWeight${index + 1}`], formData[`linearityUnload${index + 1}`])} ${getRequiredInputClass(`linearityUnload${index + 1}`)}`}
                                    />
                                </React.Fragment>
                            ))}
                        </div>
                    </>
                )}

                {/* Conditionally Rendered Fields for Corners Test */}
                {formData.cornersTest && (
                    <>
                        <div className="corners-header">
                            <label>{t.cornersTest}</label>
                            <input 
                                type="number" 
                                name="cornerWeightValue" 
                                value={formData.cornerWeightValue} 
                                onChange={handleChange} 
                                placeholder={t.weightValue}
                                className={`numeric-input ${getRequiredInputClass("cornerWeightValue")}`}
                            />
                        </div>
                        <div className="corners-grid">
                            <input
                                type="number"
                                name="cornerTopLeft"
                                value={formData.cornerTopLeft}
                                onChange={handleChange}
                                placeholder={t.topLeft}
                                className={`numeric-input ${getDeviationClass(formData.cornerWeightValue, formData.cornerTopLeft)} ${getRequiredInputClass("cornerTopLeft")}`}
                            />

                            <input
                                type="number"
                                name="cornerTopRight"
                                value={formData.cornerTopRight}
                                onChange={handleChange}
                                placeholder={t.topRight}
                                className={`numeric-input ${getDeviationClass(formData.cornerWeightValue, formData.cornerTopRight)} ${getRequiredInputClass("cornerTopRight")}`}
                            />

                            <input
                                type="number"
                                name="cornerBottomLeft"
                                value={formData.cornerBottomLeft}
                                onChange={handleChange}
                                placeholder={t.bottomLeft}
                                className={`numeric-input ${getDeviationClass(formData.cornerWeightValue, formData.cornerBottomLeft)} ${getRequiredInputClass("cornerBottomLeft")}`}
                            />

                            <input
                                type="number"
                                name="cornerBottomRight"
                                value={formData.cornerBottomRight}
                                onChange={handleChange}
                                placeholder={t.bottomRight}
                                className={`numeric-input ${getDeviationClass(formData.cornerWeightValue, formData.cornerBottomRight)} ${getRequiredInputClass("cornerBottomRight")}`}
                            />
                        </div>
                    </>
                )}

                {/* Repeatability Test */}
                {formData.repeatabilityTest && (
                    <>
                        <label>{t.repeatabilityTest}</label>

                        {/* Checkbox and text on the same line */}
                        {/* <div className="checkbox-inline">
                            <input type="checkbox" checked={repeatabilityAddMoreLines} onChange={(e) => setRepeatabilityAddMoreLines(e.target.checked)} />
                            <span>{t.addMoreLines}</span>
                        </div> */}
                        <div className="repeatability-grid">
                        {
                            [...Array(formData.accuracyClass === 3 || formData.accuracyClass === 4 ? 3 : defaultRows)].map((_, index) => (
                            <React.Fragment key={index}>
                                <input
                                    type="number"
                                    name={`repeatWeight${index + 1}`}
                                    value={formData[`repeatWeight${index + 1}`] || ""}
                                    onChange={handleChange}
                                    placeholder={`${t.weight} ${index + 1}`}
                                    className={`numeric-input ${getRequiredInputClass(`repeatWeight${index + 1}`)}`}
                                />
                                <input
                                    type="number"
                                    name={`repeatLoad${index + 1}`}
                                    value={formData[`repeatLoad${index + 1}`] || ""}
                                    onChange={handleChange}
                                    placeholder={`${t.load} ${index + 1}`}
                                    className={`numeric-input ${getDeviationClass(formData[`repeatWeight${index + 1}`], formData[`repeatLoad${index + 1}`])} ${getRequiredInputClass(`repeatLoad${index + 1}`)}`}
                                />
                            </React.Fragment>
                            ))
                        }
                        </div>

                    </>
                )}

                {/* Conditionally Rendered Fields for Tare Test */}
                {formData.tareTest && (
                    <>
                        <label>{t.tareTest}</label>
                        <div className="weight-load-group">
                            <input 
                                type="number" 
                                name="weightValue" 
                                value={formData.weightValue} 
                                onChange={handleChange} 
                                placeholder={t.weightValue}
                                className={`numeric-input ${getRequiredInputClass("weightValue")}`}
                            />

                            <input
                                type="text"
                                name="loadValue"
                                value={formData.loadValue}
                                onChange={handleChange}
                                placeholder={t.loadValueTare}
                                dir="ltr" // fixes negative in hebrew
                                className={`numeric-input ${getDeviationClass(formData.weightValue, formData.loadValue, 'tare')} ${getRequiredInputClass("loadValue")}`}
                            />

                        </div>
                    </>
                )}

                {/* Symbolled Printing */}
                <label>{t.symbolled}</label>
                <div className="radio-group">
                    <label>
                        <input 
                            type="radio" 
                            name="symbolledPrinting" 
                            value="Yes" 
                            checked={formData.symbolledPrinting === 'Yes'} 
                            onChange={handleChange} 
                            disabled={SAMLIL === 'Yes'} // Disable if initial was Yes
                        />
                        {t.yes}
                    </label>
                    <label>
                        <input 
                            type="radio" 
                            name="symbolledPrinting" 
                            value="No" 
                            checked={formData.symbolledPrinting === 'No'} 
                            onChange={handleChange} 
                            disabled={SAMLIL === 'Yes'} // Disable if initial was Yes
                        />
                        {t.no}
                    </label>
                </div>

                {/* Next Calibration in Months */}
                <label>{t.nextCalibration}</label>
                <input 
                    type="number" 
                    name="nextCalibrationMonths"
                    value={formData.nextCalibrationMonths}
                    onChange={handleChange}
                    className={`numeric-input ${getRequiredInputClass("nextCalibrationMonths")}`}
                />

                {/* Temperature & Humidity End Inputs */}
                <label>{t.temperatureEnd}</label>
                <input 
                    type="number" 
                    name="temperatureEnd" 
                    value={formData.temperatureEnd} 
                    onChange={handleChange} 
                    placeholder={t.enterTemperatureEnd}
                    className={`numeric-input ${getRequiredInputClass("temperatureEnd")}`}
                />

                <label>{t.humidityEnd}</label>
                <input 
                    type="number" 
                    name="humidityEnd" 
                    value={formData.humidityEnd} 
                    onChange={handleChange} 
                    placeholder={t.enterHumidityEnd}
                    className={`numeric-input ${getRequiredInputClass("humidityEnd")}`}
                />

                <label>{t.generalComments}</label>
                <input 
                    type="text" 
                    name="comments" 
                    value={formData.comments} 
                    onChange={handleChange} 
                    placeholder={t.enterComments}
                />

                {/* Spacer for mobile view */}
                <div className="mobile-spacer"></div>

                {isUploading && (
                    <div className="uploading-message">{t.uploading}</div>
                )}

                <h4>{t.reviewSummary}</h4>

                <label>{t.visual}</label>
                <select 
                    name="visualReview" 
                    value={formData.visualReview} 
                    onChange={handleChange}
                >
                    <option value="">{t.select}</option>
                    <option value="OK">{t.ok}</option>
                    <option value="NotOK">{t.notOk}</option>
                    <option value="NotExist">{t.notExist}</option>
                </select>

                <label>{t.reset}</label>
                <select 
                    name="resetMechanism" 
                    value={formData.resetMechanism} 
                    onChange={handleChange}
                >
                    <option value="">{t.select}</option>
                    <option value="OK">{t.ok}</option>
                    <option value="NotOK">{t.notOk}</option>
                    <option value="NotExist">{t.notExist}</option>
                </select>

                <label>{t.tareMechanism}</label>
                <select 
                    name="tareMechanism" 
                    value={formData.tareMechanism} 
                    onChange={handleChange}
                >
                    <option value="">{t.select}</option>
                    <option value="OK">{t.ok}</option>
                    <option value="NotOK">{t.notOk}</option>
                    <option value="NotExist">{t.notExist}</option>
                </select>

                <label>{t.leveling}</label>
                <select 
                    name="leveling" 
                    value={formData.leveling} 
                    onChange={handleChange}
                >
                    <option value="">{t.select}</option>
                    <option value="OK">{t.ok}</option>
                    <option value="NotOK">{t.notOk}</option>
                    <option value="NotExist">{t.notExist}</option>
                </select>

                <label>{t.printing}</label>
                <select 
                    name="printing" 
                    value={formData.printing} 
                    onChange={handleChange}
                >
                    <option value="">{t.select}</option>
                    <option value="OK">{t.ok}</option>
                    <option value="NotOK">{t.notOk}</option>
                    <option value="NotExist">{t.notExist}</option>
                </select>

                {/* Use Loading Permission Checkbox & Input */}
                <div className="checkbox-group">
                    <input 
                        type="checkbox" 
                        name="useLoadingPermission" 
                        checked={formData.useLoadingPermission} 
                        onChange={handleChange} 
                    />
                    <label htmlFor="useLoadingPermission">{t.useLoadingPermission}</label>

                    {/* Show input only when checkbox is checked */}
                    {formData.useLoadingPermission && (
                        <input 
                            type="number" 
                            name="loadingPermissionValue" 
                            value={formData.loadingPermissionValue || ""} 
                            onChange={handleChange} 
                            placeholder={t.enterValue}
                            min="1"
                            className="permission-input numeric-input"
                        />
                    )}
                </div>

                {/* Use Check Up To Checkbox & Input */}
                <div className="checkbox-group">
                    <input 
                        type="checkbox" 
                        name="useCheckUpTo" 
                        checked={formData.useCheckUpTo} 
                        onChange={handleChange} 
                    />
                    <label htmlFor="useCheckUpTo">{t.useCheckUpTo}</label>

                    {/* Show input only when checkbox is checked */}
                    {formData.useCheckUpTo && (
                        <input 
                            type="number" 
                            name="checkUpToValue" 
                            value={formData.checkUpToValue || ""} 
                            onChange={handleChange} 
                            placeholder={t.enterValue}
                            min="1"
                            className="permission-input numeric-input"
                        />
                    )}
                </div>

                {/* Calibration Approval Mandatory Checkbox */}
                <div className="checkbox-group">
                    <input
                        type="checkbox"
                        name="calibrationApproval"
                        checked={formData.calibrationApproval}
                        onChange={handleChange}
                    />
                    <label htmlFor="calibrationApproval">{t.calibrationApproval}</label>
                </div>

                {/* Submit Button */}
                <div className="button-container">
                    <button type="submit" disabled={isUploading || hasSubmitted}>
                        {isUploading ? t.uploading : t.submitReport}
                    </button>
                </div>
                
                {/* PopUp Notifications using Toast */}
                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    toastClassName="custom-toast"
                    closeButton={true} // use default button
                />

            </form>
        </div>
    );
};

export default NewReport;
